/**
 * mcp-bridge 线协议 —— cron 脚本 ↔ 主进程 loopback 调用桥的**唯一**跨进程契约。
 *
 * 业务背景（大白话）：员工挂在 cron 上的业务脚本（例如「每天早上把聚光昨天的流水
 * 拉下来算一遍」）今天拿不到任何 MCP 连接器身份——脚本自己发 HTTP 必 401。改后
 * 脚本不再持有任何密钥，而是凭一张「每 run 一次性」的票回打本机 Bridge，由主进程
 * 代它执行已授权连接器的工具调用。脚本零凭证、零 401、长脚本身份无感续期。
 *
 * 设计单源：`docs/design/capability-supply-surface-2026-08-25.md` §八（A2′ 定稿）
 * + §三（身份模型）；PRD `docs/_temp/prd-mcp-bridge.md` E1-E8。
 *
 * 序列化单一真相源（红线 #7 / 架构不变量 #7）：请求/响应两个方向的结构只有这里
 * 一份 typed 定义 + 一对反序列化 guard。Bridge 服务端（packages/core cron/
 * mcp-bridge.ts）与 `dosia-mcp` CLI shim 两端都从这里取，禁止任何一端手搓
 * object literal 或用 `as` 代替校验——两端跨**进程**，输入一律是不可信字节。
 *
 * 纯数据 + 纯函数，无 I/O（同 connector-handshake.ts / mcp-plugin-name.ts）。
 */
/** 单个请求体的字节上限（Bridge 侧读 body 时同用）。业务脚本传的是工具参数，
 *  不是文件——1MB 足够，且给 loopback 端口一个明确的拒绝面。 */
export const MCP_BRIDGE_MAX_REQUEST_BYTES = 1_048_576;
function isPlainRecord(v) {
    return typeof v === "object" && v !== null && !Array.isArray(v);
}
/**
 * 唯一反序列化 guard：把进程外送来的任意 JSON 收敛成 {@link McpBridgeCallRequest}。
 *
 * 严格拒绝而不是宽松补全——调用方是员工手写脚本，含糊的「猜你想调哪个 server」
 * 会把一次拼写错误变成一次真实的对外调用。未知字段直接丢弃（不回显，避免把
 * 攻击者塞进来的内容原样带进日志/响应）。
 */
export function parseMcpBridgeCallRequest(input) {
    if (!isPlainRecord(input))
        return { ok: false, reason: "body must be a JSON object" };
    const server = input.server;
    if (typeof server !== "string" || server.trim().length === 0) {
        return { ok: false, reason: "field \"server\" must be a non-empty string" };
    }
    const tool = input.tool;
    if (typeof tool !== "string" || tool.trim().length === 0) {
        return { ok: false, reason: "field \"tool\" must be a non-empty string" };
    }
    const rawArgs = input.arguments;
    if (rawArgs !== undefined && !isPlainRecord(rawArgs)) {
        return { ok: false, reason: "field \"arguments\" must be a JSON object when present" };
    }
    const value = { server: server.trim(), tool: tool.trim() };
    if (rawArgs !== undefined)
        value.arguments = rawArgs;
    return { ok: true, value };
}
// ── 错误码 ──────────────────────────────────────────────────────────────────
/** 全部错误码（值即线上字面量）。数组是单源，`McpBridgeErrorCode` 由它派生。 */
export const MCP_BRIDGE_ERROR_CODES = [
    /** 请求形状不合法：非 POST / 非 JSON / Host 头非本机 / body guard 失败。 */
    "BAD_REQUEST",
    /** token 缺失、错误、或该 run 已注销。 */
    "UNAUTHORIZED",
    /** 声明的连接器在本机解析不出（未挂载 / 凭证缺失 / 物化失败）。 */
    "CONNECTOR_UNAVAILABLE",
    /** 工具命中无人值守禁令单，或不在本任务 allowedTools 内。 */
    "TOOL_FORBIDDEN",
    /** 员工身份已过期且重签仍失败。 */
    "IDENTITY_EXPIRED",
    /** 该 run 未声明这个 server，或上游服务端 403（收权/越权）。 */
    "FORBIDDEN",
    /** 上游 MCP 调用失败（其余一切上游错误）。 */
    "UPSTREAM_ERROR",
    /** 单次调用超时。 */
    "TIMEOUT",
];
const ERROR_CODE_SET = new Set(MCP_BRIDGE_ERROR_CODES);
export function isMcpBridgeErrorCode(v) {
    return typeof v === "string" && ERROR_CODE_SET.has(v);
}
/**
 * 唯一反序列化 guard（shim 侧消费）：Bridge 崩了/被别的本机进程顶了端口时，
 * shim 收到的可能是任意字节，不能 `as` 成响应就往下走。
 */
export function parseMcpBridgeCallResponse(input) {
    if (!isPlainRecord(input))
        return { ok: false, reason: "response must be a JSON object" };
    if (typeof input.ok !== "boolean") {
        return { ok: false, reason: "field \"ok\" must be a boolean" };
    }
    if (input.ok) {
        const result = input.result;
        if (!isPlainRecord(result) || !Array.isArray(result.content)) {
            return { ok: false, reason: "success response must carry result.content[]" };
        }
        return { ok: true, value: { ok: true, result: result } };
    }
    const error = input.error;
    if (!isPlainRecord(error)) {
        return { ok: false, reason: "failure response must carry an error object" };
    }
    if (!isMcpBridgeErrorCode(error.code)) {
        return { ok: false, reason: `unknown error code: ${String(error.code)}` };
    }
    const message = typeof error.message === "string" ? error.message : "";
    return { ok: true, value: { ok: false, error: { code: error.code, message } } };
}
/** 唯一失败响应构建器 —— 服务端禁手搓 object literal（红线 #7）。 */
export function buildMcpBridgeErrorResponse(code, message) {
    return { ok: false, error: { code, message } };
}
// ── 退出码契约（`dosia-mcp` CLI shim 消费） ─────────────────────────────────
/**
 * shim 进程退出码表。
 *
 * 【口径来源】`3` = 「声明的连接器不可用」是 **2026-08-25 用户拍板事实**
 * （交接稿成功标准 2 / PRD E2）。其余码位对齐张晓杰《技术方案》§6.1.B——
 * **该原文不在仓内，属【待核实】**；在拿到原文并对账之前，**本常量表就是仓内
 * 单源**，任何一端（shim / 文档 / e2e 判据）都必须从这里取，不得复刻字面量。
 * 若日后原文与此有出入，改这一处 + 其消费方，不新建第二份表。
 */
export const MCP_BRIDGE_EXIT_CODES = {
    /** 调用成功。注意：工具自身返回 `isError:true` 在**线协议**上仍是「桥成功」
     *  （`ok:true` 携带失败结果），但 `dosia-mcp` shim 会把它映射成退出码 5
     *  （UPSTREAM_ERROR，结果 JSON 照常写 stdout）——cron 假成功是 B1 根治的
     *  bug 类，工具失败必须在退出码上可见（2026-08-25 裁决）。 */
    OK: 0,
    /** 用法错误：参数缺失/JSON 坏/不在 cron 脚本上下文里（env 没注入）。 */
    USAGE: 2,
    /** 连接器不可用 —— 拍板事实。 */
    CONNECTOR_UNAVAILABLE: 3,
    /** 身份过期 / 被收权 / 工具被禁：脚本不该重试，直接终止。 */
    IDENTITY_OR_FORBIDDEN: 4,
    /** 上游 MCP 调用失败。 */
    UPSTREAM_ERROR: 5,
    /** 单次调用超时。 */
    TIMEOUT: 6,
    /** shim/桥内部错误（不该发生的那一类）。 */
    INTERNAL: 70,
};
/**
 * 错误码 → 退出码的**唯一**映射（shim 直接用，禁在 shim 里再写一遍 switch）。
 *
 * `UNAUTHORIZED` 归 4：从脚本视角，「我这张票不被认」与「我身份没了」是同一类
 * ——都不该重试，且都不是脚本参数写错。`TOOL_FORBIDDEN` 同理归 4（禁令面）。
 */
export function mcpBridgeExitCodeForError(code) {
    switch (code) {
        case "BAD_REQUEST":
            return MCP_BRIDGE_EXIT_CODES.USAGE;
        case "CONNECTOR_UNAVAILABLE":
            return MCP_BRIDGE_EXIT_CODES.CONNECTOR_UNAVAILABLE;
        case "UNAUTHORIZED":
        case "TOOL_FORBIDDEN":
        case "IDENTITY_EXPIRED":
        case "FORBIDDEN":
            return MCP_BRIDGE_EXIT_CODES.IDENTITY_OR_FORBIDDEN;
        case "UPSTREAM_ERROR":
            return MCP_BRIDGE_EXIT_CODES.UPSTREAM_ERROR;
        case "TIMEOUT":
            return MCP_BRIDGE_EXIT_CODES.TIMEOUT;
        default:
            return MCP_BRIDGE_EXIT_CODES.INTERNAL;
    }
}
// ── host 注入 env 名 ────────────────────────────────────────────────────────
/**
 * 每 run 一次性票据的 env 名。
 *
 * **这是 host 在 spawn 那一刻注入的，不是任务声明面**：绝不允许加进
 * `SCRIPT_ENV_ALLOWLIST`（execution-contract.ts）——一旦进了声明面，任务作者
 * 就能自己写一个 `DOSIA_JOB_TOKEN`，票据的「由 host 现铸、绑定本次 run」语义
 * 当场作废。token 不落盘、不打日志、run 结束立即注销。
 */
export const MCP_BRIDGE_TOKEN_ENV = "DOSIA_JOB_TOKEN";
/** Bridge 的 loopback 端口 env 名。同上：host 注入，禁进 SCRIPT_ENV_ALLOWLIST。 */
export const MCP_BRIDGE_PORT_ENV = "DOSIA_BRIDGE_PORT";
/** Bridge 唯一的请求路径。两端共用，禁字面量散落。 */
export const MCP_BRIDGE_CALL_PATH = "/call";
//# sourceMappingURL=mcp-bridge-contract.js.map